import { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../App';
import { submitFeedback, type FeedbackContext } from '../api/client';
import type { Candidate, InterviewSession } from '../types';

const SUPPORT_EMAIL = 'help@callbaba.com';

interface FeedbackStateShape {
  candidate: Candidate | null;
  interviewSession: InterviewSession | null;
}

function buildFeedbackContext(state: FeedbackStateShape, pathname: string): FeedbackContext {
  const viewport =
    typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : undefined;
  return {
    page: pathname,
    stage: state.interviewSession?.stage ?? null,
    interview_id: state.interviewSession?.id ?? null,
    current_step: state.candidate?.current_step ?? null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    viewport,
    referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
  };
}

/** Show one support control on each candidate page. */
export function FeedbackWidget() {
  const { state } = useContext(AppContext);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState(false);

  const candidateId = state.candidate?.id;

  if (location.pathname.startsWith('/admin')) return null;

  const handleSubmit = async () => {
    if (!comment.trim() || !candidateId) return;
    setSending(true);
    setSendError(false);
    try {
      await submitFeedback(
        candidateId,
        comment.trim(),
        buildFeedbackContext(state, location.pathname),
      );
      setSent(true);
      setComment('');
      setTimeout(() => {
        setSent(false);
        setOpen(false);
      }, 2000);
    } catch {
      setSendError(true);
    } finally {
      setSending(false);
    }
  };

  // Hide the control until the candidate starts a session.
  if (!candidateId) return null;

  return (
    <>
      {/* Support button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 left-4 z-[70] flex items-center gap-2
                     rounded-full bg-sage px-4 py-3 text-white shadow-lg sm:bottom-6 sm:left-6
                     transition-transform hover:scale-105 active:scale-95"
          aria-label="Contact technical support"
          aria-expanded={false}
          aria-controls="technical-support-panel"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span className="text-sm font-medium">Get help</span>
        </button>
      )}

      {/* Support panel */}
      {open && (
        <div
          id="technical-support-panel"
          role="dialog"
          aria-label="Technical support"
          className="fixed bottom-4 left-4 z-[70] w-[calc(100vw-2rem)] max-w-80 animate-slide-up rounded-2xl border
                     border-border bg-canvas shadow-xl ring-1 ring-border/50 sm:bottom-6 sm:left-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
            <span className="text-sm font-medium text-ink">Technical support</span>
            <button
              type="button"
              onClick={() => { setOpen(false); setSent(false); setSendError(false); }}
              className="text-muted hover:text-ink transition-colors"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-4 py-3">
            {sent ? (
              <div className="flex items-center gap-2 py-4 text-sm text-sage-dark">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                We saved your report for our technical team.
              </div>
            ) : (
              <>
                <p className="mb-3 text-xs text-muted">
                  Tell us what happened. We include this page and your browser details.
                </p>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Describe the issue and what you saw..."
                  rows={3}
                  maxLength={2000}
                  className="w-full resize-none rounded-xl border border-border bg-surface/50 px-3 py-2
                             text-sm text-ink placeholder:text-muted/60 focus:border-sage focus:outline-none
                             focus:ring-1 focus:ring-sage/30"
                  autoFocus
                />
                {sendError && (
                  <p className="mt-2 text-xs text-danger" role="alert">
                    We could not send this report. Please email{' '}
                    <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                      {SUPPORT_EMAIL}
                    </a>.
                  </p>
                )}
                <div className="mt-3 flex items-center justify-between gap-3">
                  <a
                    className="text-xs text-muted underline underline-offset-2 hover:text-ink"
                    href={`mailto:${SUPPORT_EMAIL}?subject=Interview%20Support%20Request`}
                  >
                    Email instead
                  </a>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!comment.trim() || sending}
                    className="rounded-full bg-sage px-4 py-1.5 text-xs font-medium text-white
                               transition-all hover:bg-sage-dark disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {sending ? 'Sending...' : 'Send to support'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}


/**
 * Explicit feedback prompt — use on Results or completion pages.
 * Shows inline (not floating) with a text area.
 */
export function FeedbackPrompt({ title, subtitle }: { title?: string; subtitle?: string }) {
  const { state } = useContext(AppContext);
  const location = useLocation();
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const candidateId = state.candidate?.id;
  if (!candidateId) return null;

  const handleSubmit = async () => {
    if (!comment.trim()) return;
    setSending(true);
    try {
      await submitFeedback(
        candidateId,
        comment.trim(),
        buildFeedbackContext(state, location.pathname),
      );
      setSent(true);
      setComment('');
    } catch {
      // Silent
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="mt-6 flex items-center gap-2 text-sm text-sage-dark">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 6L9 17l-5-5" />
        </svg>
        Thanks for your feedback!
      </div>
    );
  }

  return (
    <div className="mt-8 w-full max-w-lg rounded-2xl border border-border bg-surface/60 p-5">
      <p className="mb-1 text-base font-medium text-ink">
        {title || 'How was your experience?'}
      </p>
      <p className="mb-3 text-xs text-muted">
        {subtitle || 'Your feedback helps us improve. Let us know if anything went wrong or what we did well.'}
      </p>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Share your thoughts..."
        rows={3}
        maxLength={2000}
        className="w-full resize-none rounded-xl border border-border bg-canvas px-4 py-3
                   text-sm text-ink placeholder:text-muted/60 focus:border-sage focus:outline-none
                   focus:ring-1 focus:ring-sage/30"
      />
      <div className="mt-3 flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={!comment.trim() || sending}
          className="rounded-full bg-sage px-5 py-2 text-sm font-medium text-white
                     transition-all hover:bg-sage-dark disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? 'Sending...' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );
}
